import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { interval, firstValueFrom, of } from 'rxjs';
import { map, filter, timeout, take, catchError } from 'rxjs/operators';
import { UnpaidPendingCallService } from '@services/pending-call-banner-service';
import { PendingCallDeepLinkConfig, DeepLinkResult, PendingCallBannerState } from '@services/pending-call-banner-service/types';

@Injectable({
  providedIn: 'root',
})
export class DeepLinkService {
  private readonly BANNER_CHECK_TIMEOUT = 5000; // 5 seconds

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly unpaidPendingCallService: UnpaidPendingCallService
  ) {}

  /**
   * Checks if pending call deep link should be processed
   */
  shouldProcessPendingCallDeepLink(): boolean {
    return this.route.snapshot.queryParams['openCallRequest'] === 'true';
  }

  /**
   * Determines target class for regimen navigation (used by app.component)
   * Respects openCallRequest query param and uses class from query params if present
   */
  determineTargetClassForPendingCall(hasFaceRegimen: boolean, appConfig: any): string {
    const { FACE, HAIR } = appConfig.Shared.InstantCheckup.Type;
    const openCallRequest = this.route.snapshot.queryParams['openCallRequest'];
    const queryParamsClass = this.route.snapshot.queryParams['class'];

    if (openCallRequest && queryParamsClass) {
      return queryParamsClass;
    }
    return hasFaceRegimen ? FACE : HAIR;
  }

  /**
   * Processes the pending call deep link flow - opens bottomsheet automatically
   */
  async processPendingCallDeepLink(config: PendingCallDeepLinkConfig): Promise<DeepLinkResult> {
    if (!this.shouldProcessPendingCallDeepLink()) {
      return { success: false, reason: 'no_deep_link' };
    }

    try {
      await this.removeQueryParam();

      const bannerElement = await this.waitForBannerReady();
      if (!bannerElement) {
        return { success: false, reason: 'banner_not_ready' };
      }

      if (!config.bannerState || !this.shouldShowBanner(config.bannerState)) {
        return { success: false, reason: 'banner_not_eligible' };
      }

      await this.scrollToBanner(bannerElement);

      await new Promise(resolve => setTimeout(resolve, 600));

      const success = await this.unpaidPendingCallService.openRequestCallBottomsheet(
        config.regimen.objectId,
        config.regimen.class,
        config.variantData?.noOfHours || 1,
        config.slot
      );

      return { success, reason: success ? 'opened' : 'failed' };
    } catch (error) {
      return { success: false, reason: 'error', error };
    }
  }

  /**
   * Waits for banner container to be rendered in DOM and returns the request banner element
   */
  private async waitForBannerReady(): Promise<Element | null> {
    return firstValueFrom(
      interval(100).pipe(
        map(() => {
          const container = document.querySelector('app-unpaid-user-doctor-call-banner-container');
          const requestBanner = document.querySelector('app-request-call-banner');
          return container && requestBanner ? requestBanner : null;
        }),
        filter(element => element !== null),
        timeout(this.BANNER_CHECK_TIMEOUT),
        take(1),
        catchError(() => of(null))
      )
    );
  }

  /**
   * Scrolls to the banner element with smooth animation
   */
  private async scrollToBanner(element: Element): Promise<void> {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await new Promise(resolve => setTimeout(resolve, 100));
    window.scrollBy({ top: -40, behavior: 'smooth' });
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  /**
   * Removes the openCallRequest query parameter from URL
   */
  private async removeQueryParam(): Promise<void> {
    await this.router.navigate([], {
      queryParams: { openCallRequest: null },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Checks if banner should be shown for deep link
   */
  private shouldShowBanner(state: PendingCallBannerState): boolean {
    return state.isEligible &&
           !state.hideBanner &&
           !state.status;
  }
}

